<lvr-heading>
	{{ 'institution.persons.edit.title' | transloco }}
</lvr-heading>
<form
	class="form-container"
	[formGroup]="form"
	lvrAutoSave="institution-person"
	[entityId]="draftId()"
	[autoLoad]="false"
>
	<section class="form-grid">
		<!-- Category Dropdown Field -->
		@let catCtrl = form.controls.categoryId;
		<lvr-field [label]="'institution.persons.edit.category' | transloco | appendAsterisk">
			<lvr-searchable-select
				formControlName="categoryId"
				[options]="personTypes()"
				[optionLabel]="personTypeLabel"
				[placeholder]="'base.field.select_please' | transloco"
				[searchPlaceholder]="'base.field.search_placeholder' | transloco"
				[noResultsText]="'base.field.no_results' | transloco"
				[ariaLabel]="'institution.person_create.category' | transloco"
				[required]="true"
				[hasError]="hasError(catCtrl, 'required')"
				[errorMessage]="'base.form.error.category_required' | transloco"
			></lvr-searchable-select>
		</lvr-field>

		<lvr-responsibility
			formControlName="responsibleBranches"
			[branches]="branches()"
			[label]="'institution.persons.edit.responsibleBranchLabel' | transloco"
		></lvr-responsibility>

		<!-- Last Name Field -->
		@let lastNameCtrl = form.controls.lastName;
		<lvr-field [label]="'institution.persons.edit.last_name' | transloco | appendAsterisk">
			<mat-form-field lvrInput [lvrError]="hasError(lastNameCtrl, 'maxLength', 'required', 'pattern')">
				<input
					matInput
					formControlName="lastName"
					[placeholder]="'institution.persons.edit.last_name_placeholder' | transloco"
					[errorStateMatcher]="matcher"
					aria-required="true"
				/>
				@if (hasError(lastNameCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: 50 } }}</mat-error>
				} @else if (hasError(lastNameCtrl, 'required')) {
					<mat-error>{{ 'base.form.error.last_name_required' | transloco }}</mat-error>
				} @else if (hasError(lastNameCtrl, 'pattern')) {
					<mat-error>{{ 'base.form.error.last_name_invalid' | transloco }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>

		<!-- First Name Field -->
		@let firstNameCtrl = form.controls.firstName;
		<lvr-field [label]="'institution.persons.edit.first_name' | transloco">
			<mat-form-field lvrInput [lvrError]="hasError(firstNameCtrl, 'maxLength', 'pattern')">
				<input
					matInput
					formControlName="firstName"
					[placeholder]="'institution.persons.edit.first_name_placeholder' | transloco"
					[errorStateMatcher]="matcher"
				/>
				@if (hasError(firstNameCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: 50 } }}</mat-error>
				} @else if (hasError(firstNameCtrl, 'pattern')) {
					<mat-error>{{ 'base.form.error.first_name_invalid' | transloco }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>

		<!-- Gender Field -->
		<lvr-field [label]="'institution.persons.edit.gender' | transloco">
			<mat-form-field lvrInput [attr.aria-label]="'base.field.gender' | transloco">
				<mat-select
					formControlName="genderId"
					[placeholder]="'base.field.select_please' | transloco"
					[errorStateMatcher]="matcher"
				>
					@for (option of genders(); track option.id) {
						<mat-option
							[value]="option.id"
							matTooltipClass="info-tooltip"
							[matTooltip]="
								option.salutation === 'divers u.a.' ? ('client.create_client.gender_hint_divers' | transloco) : null
							"
						>
							{{ option.salutation }}
						</mat-option>
					}
				</mat-select>
			</mat-form-field>
		</lvr-field>

		<!-- Title Field -->
		@let titleCtrl = form.controls.title;
		<lvr-field [label]="'institution.persons.edit.person_title' | transloco">
			<mat-form-field lvrInput [lvrError]="hasError(titleCtrl, 'maxLength', 'pattern')">
				<input
					matInput
					formControlName="title"
					[placeholder]="'institution.persons.edit.person_title_placeholder' | transloco"
					[errorStateMatcher]="matcher"
				/>
				@if (hasError(titleCtrl, 'pattern')) {
					<mat-error>{{ 'base.form.error.title_invalid' | transloco }}</mat-error>
				}
				@if (hasError(titleCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: 50 } }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>

		<!-- Address -->
		<lvr-address-completion
			class="address-completion"
			formControlName="address"
			[showAsterisk]="false"
		></lvr-address-completion>

		<!-- Remark Field -->
		@let remarkCtrl = form.controls.remark;
		<lvr-field [label]="'institution.persons.edit.remark' | transloco">
			<mat-form-field lvrInput [lvrError]="hasError(remarkCtrl, 'maxLength')">
				<textarea
					matInput
					formControlName="remark"
					[placeholder]="'institution.persons.edit.remark_placeholder' | transloco"
					class="taller-textarea"
					[maxlength]="remarkCtrlMaxLength"
				></textarea>
				<mat-hint>{{
					'base.form.hint.max_length' | transloco: { actual: remarkCtrl?.value?.length || 0, max: remarkCtrlMaxLength }
				}}</mat-hint>
				@if (hasError(remarkCtrl, 'maxLength')) {
					<mat-error>{{ 'base.form.error.max_length_exceeded' | transloco: { max: remarkCtrlMaxLength } }}</mat-error>
				}
			</mat-form-field>
		</lvr-field>
	</section>

	<!-- Communication section -->
	@for (item of communicationWays; track item; let index = $index) {
		<section class="form-grid">
			<h3 lvrPadding class="additional-communication-heading" [padVertical]="0" [padHorizontal]="8">
				<span>{{ 'base.field.contact_point.title' | transloco }}</span>
				<button matIconButton class="btn btn-tertiary" (click)="removeCommunicationWay(index)">
					<mat-icon class="material-symbols-outlined" aria-hidden="true">delete</mat-icon>
				</button>
			</h3>
			<lvr-contact-point
				[contactPointCategories]="contactPointCategories()"
				class="address-completion"
				[initValue]="communicationWays[$index]"
				(values)="updateValues($event, $index)"
			></lvr-contact-point>
		</section>
	}

	<!-- Add additional communication way button -->
	<section class="form-grid">
		<lvr-button
			variant="primary"
			[label]="'base.field.contact_point.title' | transloco"
			[ariaLabel]="'base.field.contact_point.add_communication' | transloco"
			[type]="'button'"
			(clicked)="addCommunicationWay()"
			icon="add"
		></lvr-button>
	</section>

	<!-- Privacy Field -->
	<lvr-data-protection
		formControlName="clarification"
		[showAsterisk]="false"
		[locked]="isClarificationFinal()"
	></lvr-data-protection>

	<lvr-default-actions
		[disableSave]="form.invalid || !canEditInstitutionPersonsPermission()"
		[showLoading]="saving()"
		(save)="save()"
		(cancelSave)="cancel()"
	>
		<lvr-restore-actions entityType="institution-person" [entityId]="draftId()"></lvr-restore-actions>
	</lvr-default-actions>
</form>
